import { useEffect, useState, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Separator } from "@/components/ui/separator";
import { Location } from "@/components/campaign/types";
import CalendarSection from "./calendar/CalendarSection";
import WakiWarning from "./campaign/components/warnings/WakiWarning";
import DurationHeader from "./calendar/DurationHeader";
import { useCampaignDuration } from "./calendar/hooks/useCampaignDuration";
import { DurationCalculationService } from "./calendar/services/DurationCalculationService";
import { DateStorageService } from "./calendar/services/DateStorageService";
import DateSelectionModeToggle from "./calendar/DateSelectionModeToggle";
import MultiDateCalendar from "./calendar/MultiDateCalendar";
import { useMultiDateSelection, DateSelectionMode } from "./calendar/hooks/useMultiDateSelection";

interface DurationSliderProps {
  value: number;
  onChange: (value: number[]) => void;
  selectedLocations?: Location[];
  autoHighlight?: boolean; // Only highlight dates when this is true (AI suggestions)
}

const DurationSlider = ({
  value,
  onChange,
  selectedLocations = [],
  autoHighlight = false
}: DurationSliderProps) => {
  const { language, t } = useLanguage();
  
  // Stable no-op callback for inactive hooks - prevents recreation on every render
  const noOpOnChange = useCallback(() => {}, []);
  
  // Mode state with persistence
  const [mode, setMode] = useState<DateSelectionMode>(() => {
    const stored = localStorage.getItem('dateSelectionMode') as DateSelectionMode;
    return stored === 'specific' ? 'specific' : 'range';
  });
  
  // Determine which mode is active
  const isRangeMode = mode === 'range';
  
  // Range mode hook - only active when in range mode
  // Uses noOpOnChange when inactive to prevent unnecessary updates
  const {
    startDate,
    endDate,
    startDisplayDate,
    endDisplayDate,
    selectStartDate,
    selectEndDate,
    navigateCalendar
  } = useCampaignDuration(isRangeMode ? onChange : noOpOnChange, isRangeMode);
  
  // Specific dates mode hook - only active when in specific mode
  // Uses noOpOnChange when inactive to prevent unnecessary updates
  const {
    selectedDates,
    toggleDate,
    clearAllDates,
    selectAllAvailableDates,
    minDate
  } = useMultiDateSelection(!isRangeMode ? onChange : noOpOnChange, !isRangeMode);
  
  // Handle mode change
  const handleModeChange = (newMode: DateSelectionMode) => {
    setMode(newMode);
    localStorage.setItem('dateSelectionMode', newMode);
    
    // Clear the other mode's data and reset duration
    if (newMode === 'specific') {
      // Clear range data
      localStorage.removeItem('startDate');
      localStorage.removeItem('endDate');
      localStorage.removeItem('startDate_exact');
      localStorage.removeItem('endDate_exact');
    } else {
      // Clear specific dates when switching to range mode
      localStorage.removeItem('selectedDates');
      clearAllDates();
    }
    
    // Reset duration to trigger recalculation
    onChange([0]);
  };
  
  // Only auto-highlight calendar when applying AI suggestions (range mode only)
  useEffect(() => {
    if (autoHighlight && value > 0 && mode === 'range') {
      if (!startDate || !endDate) {
        // Seed dates when duration is set but no dates exist (AI suggestion)
        const { startDate: seedStart, endDate: seedEnd } = DurationCalculationService.seedDatesForDuration(value);
        DateStorageService.saveDate(seedStart, 'start');
        DateStorageService.saveDate(seedEnd, 'end');
        console.log('DurationSlider: seeded dates for AI suggestion', { 
          value, 
          start: seedStart.toISOString(), 
          end: seedEnd.toISOString() 
        });
      } else {
        // Adjust end date if duration doesn't match (AI suggestion)
        const adjustment = DurationCalculationService.adjustEndDateForDuration(startDate, endDate, value);
        if (adjustment.needsAdjustment && adjustment.newEndDate) {
          DateStorageService.saveDate(adjustment.newEndDate, 'end');
          console.log('DurationSlider: adjusted end date for AI suggestion', { 
            value, 
            end: adjustment.newEndDate.toISOString() 
          });
        }
      }
    }
  }, [autoHighlight, value, startDate?.getTime(), endDate?.getTime(), mode]);
  
  // Calculate display value based on mode
  const displayValue = mode === 'specific' ? selectedDates.length : value;
  
  return (
    <div className="w-full space-y-4">
      <DurationHeader value={displayValue} />
      
      <WakiWarning selectedLocations={selectedLocations} />
      
      {/* Mode Toggle */}
      <DateSelectionModeToggle mode={mode} onModeChange={handleModeChange} />
      
      {/* Conditional Calendar Rendering */}
      {mode === 'range' ? (
        <div className="bg-card dark:bg-card p-4 rounded-lg border border-border">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative">
            <CalendarSection
              title={t("campaignStart")}
              displayDate={startDisplayDate}
              selectedDate={startDate}
              onDaySelect={(day) => selectStartDate(day, startDisplayDate)}
              onNavigate={(direction) => navigateCalendar('start', direction)}
              isStartCalendar={true}
              startDate={startDate}
            />

            <Separator orientation="vertical" className="hidden md:block absolute left-1/2 h-full -mx-3" />
            <Separator orientation="horizontal" className="md:hidden w-full my-3" />

            <CalendarSection
              title={t("campaignEnd")}
              displayDate={endDisplayDate}
              selectedDate={endDate}
              onDaySelect={(day) => selectEndDate(day, endDisplayDate)}
              onNavigate={(direction) => navigateCalendar('end', direction)}
              isStartCalendar={false}
              startDate={startDate}
            />
          </div>
        </div>
      ) : (
        <MultiDateCalendar
          selectedDates={selectedDates}
          minDate={minDate}
          onToggleDate={toggleDate}
          onClearAll={clearAllDates}
          onSelectAllMonth={selectAllAvailableDates}
        />
      )}
    </div>
  );
};

export default DurationSlider;
